/* AH-RIN — Wishlist/Compare Enhancements (final)
   - Per-card "Add to cart" (updates Dawn sections immediately)
   - Bulk bar: Add all / Remove all
   - Instant refresh after "Remove all"
   - Normalizes per-item Remove button class for consistent styling
*/
(() => {
  /* ---------------- helpers ---------------- */

  // (Only used by older share buttons elsewhere; keeps console quiet if referenced)
  async function copyToClipboard(text) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) {}
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch (_) {
      return false;
    }
  }

  function getHandleFromCard(card) {
    const fromAttr = card.getAttribute('data-handle');
    if (fromAttr) return fromAttr.trim();
    const a = card.querySelector('a[href^="/products/"]');
    if (!a) return null;
    const href = a.getAttribute('href') || '';
    const after = href.split('/products/')[1] || '';
    return after.split('?')[0].split('#')[0].trim() || null;
  }

  function getListKeyFromGrid(grid) {
    if (grid.classList.contains('ahrin-list-grid--compare')) return 'ahrin:compare';
    if (grid.classList.contains('ahrin-list-grid--wishlist')) return 'ahrin:wishlist';
    const id = (grid.id || '').toLowerCase();
    if (id.includes('compare')) return 'ahrin:compare';
    if (id.includes('wish')) return 'ahrin:wishlist';
    const t = (grid.getAttribute('data-list') || '').toLowerCase();
    if (t === 'compare') return 'ahrin:compare';
    return 'ahrin:wishlist';
  }

  function tryOpenCartDrawer() {
    try {
      const el = document.querySelector('cart-drawer');
      if (el && typeof el.open === 'function') el.open();
    } catch (_) {}
  }

  // Sections we want Shopify to send back so Dawn can re-render instantly
  const SECTIONS = ['cart-icon-bubble', 'cart-drawer', 'cart-notification'];

function renderUpdatedSections(resp) {
  // Prefer Dawn’s components to re-render all targets
  const drawer = document.querySelector('cart-drawer');
  const notif  = document.querySelector('cart-notification');

  try {
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(resp);
    } else if (notif && typeof notif.renderContents === 'function') {
      notif.renderContents(resp);
    }
  } catch (_) { /* fall through to manual update */ }

  // Manual header bubble refresh (fallback or extra safety)
  try {
    const bubbleHTML = resp?.sections?.['cart-icon-bubble'];
    const current = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && current) {
      // Shopify returns a wrapper .shopify-section. We want its inner markup.
      const dom   = new DOMParser().parseFromString(bubbleHTML, 'text/html');
      const fresh = dom.querySelector('.shopify-section');
      if (fresh) current.innerHTML = fresh.innerHTML;
    }
  } catch (_) {}
}

  async function fetchProductAndFirstAvailableVariant(handle) {
    const res = await fetch(`/products/${handle}.js`, { cache: 'no-store' });
    if (!res.ok) return null;
    const p = await res.json();
    const v = (p.variants || []).find(v => v.available) || (p.variants || [])[0];
    return v ? { id: v.id } : null;
  }

  // Single add — ask Shopify to return updated sections so we can paint immediately
  async function addVariantId(variantId, qty = 1) {
    const body = {
      id: variantId,
      quantity: qty,
      sections: SECTIONS.join(','),
      sections_url: window.location.pathname
    };
    const res = await fetch((window.routes?.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('Add failed');
    const json = await res.json();
    renderUpdatedSections(json);
    try { publish && publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ahrin-lists', cartData: json }); } catch(_) {}
  }

  // Bulk add — /cart/add.js with items[]
  async function addManyVariantIds(items) {
    if (!items.length) return;
    const body = {
      items: items.map(it => ({ id: it.id, quantity: it.quantity || 1 })),
      sections: SECTIONS.join(','),
      sections_url: window.location.pathname
    };
    const res = await fetch((window.routes?.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('Bulk add failed');
    const json = await res.json();
    renderUpdatedSections(json);
    try { publish && publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ahrin-lists', cartData: json }); } catch(_) {}
  }

  // Notify adapters + force grid to rebuild so our button injection re-runs
  function refreshListUI(grid) {
    try { document.dispatchEvent(new Event('ahrin:lists:changed')); } catch(_) {}
    try { grid.innerHTML = ''; } catch(_) {}
  }

  /* --------------- per-card button --------------- */
  function addPerItemButtons(grid) {
    grid.querySelectorAll('article.ahrin-grid-card, article.card, article').forEach(card => {
      if (card.querySelector('.ahrin-mini-add')) return; // only once

      const handle = getHandleFromCard(card);
      if (!handle) return;

      const actions =
        card.querySelector('.ahrin-grid-card__actions, .card__buttons, .card__actions') || card;

      // Normalize any existing "Remove" so CSS can style it consistently
      const removeBtn =
        actions.querySelector('.ahrin-remove, button[aria-label="Remove"], button[name="remove"], button[data-action="remove"]');
      if (removeBtn) removeBtn.classList.add('ahrin-remove');

      // Our small Add to cart button
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ahrin-mini-add button button--secondary';
      btn.textContent = 'Add to cart';
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          const v = await fetchProductAndFirstAvailableVariant(handle);
          if (v?.id) {
            await addVariantId(v.id, 1);
            tryOpenCartDrawer(); // optional UX: show the drawer when adding
          }
        } catch (e) {
          console.error(e);
        } finally {
          btn.disabled = false;
        }
      });

      // Place just before Remove if possible
      if (removeBtn?.parentNode) {
        removeBtn.parentNode.insertBefore(btn, removeBtn);
      } else {
        actions.appendChild(btn);
      }
    });
  }

  /* --------------- bulk bar --------------- */
  function injectBulkBar(grid) {
    if (!grid || grid.previousElementSibling?.classList?.contains('ahrin-bulkbar')) return;

    const bar = document.createElement('div');
    bar.className = 'ahrin-bulkbar';
    bar.innerHTML = `
      <button type="button" class="button button--primary ahrin-add-all">Add all to cart</button>
      <button type="button" class="button button--secondary ahrin-remove-all">Remove all</button>
    `;
    grid.parentNode.insertBefore(bar, grid);

    // Add all to cart
    bar.querySelector('.ahrin-add-all').addEventListener('click', async (ev) => {
      const btn = ev.currentTarget;
      btn.disabled = true;
      try {
        const cards = [...grid.querySelectorAll('article.ahrin-grid-card, article.card, article')];
        const variants = [];
        for (const card of cards) {
          const h = getHandleFromCard(card);
          if (!h) continue;
          try {
            const v = await fetchProductAndFirstAvailableVariant(h);
            if (v?.id) variants.push({ id: v.id, quantity: 1 });
          } catch(_) {}
        }
        await addManyVariantIds(variants);
        tryOpenCartDrawer();
      } catch (e) {
        console.error(e);
      } finally {
        btn.disabled = false;
      }
    });

    // Remove all (instant refresh)
    bar.querySelector('.ahrin-remove-all').addEventListener('click', () => {
      const key = getListKeyFromGrid(grid);
      try { localStorage.setItem(key, JSON.stringify([])); } catch (_) {}
      refreshListUI(grid); // counters/list refresh immediately
    });
  }

  /* --------------- init/enhance --------------- */
  function enhance(grid) {
    if (!grid) return;
    injectBulkBar(grid);
    addPerItemButtons(grid);
  }

  function observe(grid) {
    if (!grid || grid.__ahrinObserved) return;
    grid.__ahrinObserved = true;
    const mo = new MutationObserver(() => addPerItemButtons(grid));
    mo.observe(grid, { childList: true, subtree: true });
  }

  function init() {
    const grids = document.querySelectorAll(
      '#ahrin-wishlist-grid, #arin-wishlist-grid, #ahrin-compare-grid, #arin-compare-grid, .ahrin-list-grid'
    );
    grids.forEach(g => { enhance(g); observe(g); });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('ahrin:lists:changed', () => setTimeout(init, 0));
})();
